/**
 * Vendor Bills + Payments Redux helpers (HPRMS Phase E).
 * Boot = list GET headers; Sync = refresh + payment enrich; open = Redux;
 * write = upsert (**no** verify GET); hole-fill detail.
 */

import {
  getVendorBills,
  getVendorBillById,
  getVendorPayments,
  enrichVendorBillListWithPayments,
  resolveVendorBillBalances,
  deriveVendorBillStatus,
} from "../apis/purchaseStock.api";

const getStore = () => require("../index").default;

const billKey = (b) => String(b?.POID ?? b?.id ?? "").trim();
const paymentKey = (p) => String(p?.POID ?? p?.id ?? "").trim();

const hasBillLines = (b) => Array.isArray(b?.lineItems) && b.lineItems.length > 0;

// ─── Bills ─────────────────────────────────────────────────────────

export const vendorBillsPresentInStore = (apState) => {
  const state = apState || getStore().getState()?.accountsPayableState;
  return Boolean(state?.billsLoaded);
};

export const findVendorBillInState = (apState, id) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  return (apState?.vendorBills || []).find((b) => billKey(b) === key) || null;
};

export const upsertVendorBillInStore = (bill) => {
  if (!bill) return;
  const id = bill.POID || bill.id;
  if (!id) return;
  getStore().dispatch({
    type: "UPSERT_VENDOR_BILL",
    payload: resolveVendorBillBalances({
      ...bill,
      POID: id,
      id,
    }),
  });
};

export const applyVendorBillsToStore = (list) => {
  getStore().dispatch({
    type: "SET_VENDOR_BILLS",
    payload: Array.isArray(list) ? list : [],
  });
};

export const patchVendorBillInStore = (id, patch) => {
  const key = String(id ?? "").trim();
  if (!key) return;
  getStore().dispatch({ type: "PATCH_VENDOR_BILL", id: key, patch });
};

/** Boot / light refresh — headers only (no payment enrich). */
export const refreshVendorBillsInStore = async ({
  silent = true,
  enrich = false,
} = {}) => {
  const list = await getVendorBills({});
  const next = enrich ? await enrichVendorBillListWithPayments(list || []) : list || [];
  applyVendorBillsToStore(next);
  return next;
};

/** Explicit Sync — list + allocation enrich for paid/due. */
export const refreshVendorBillsEnrichedInStore = async ({ silent = false } = {}) =>
  refreshVendorBillsInStore({ silent, enrich: true });

export const ensureVendorBillsInStore = async ({
  force = false,
  silent = true,
  enrich = false,
} = {}) => {
  if (!force && vendorBillsPresentInStore()) {
    return getStore().getState()?.accountsPayableState?.vendorBills || [];
  }
  return refreshVendorBillsInStore({ silent, enrich });
};

export const resolveVendorBillById = async (
  id,
  { forceNetwork = false, reconcile = true } = {}
) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findVendorBillInState(
      store.getState()?.accountsPayableState,
      key
    );
    if (hasBillLines(fromStore)) return fromStore;
  }
  const full = await getVendorBillById(key, { reconcile });
  if (!full) return null;
  const stamped = { ...full, __detailFetched: true };
  upsertVendorBillInStore(stamped);
  return stamped;
};

/**
 * Apply a payment allocation to bill balances in Redux (increase paid / decrease due).
 * Use after successful createVendorPayment — no bill verify GET.
 */
export const applyPaymentAllocationsToBillsInStore = (allocations = [], { reverse = false } = {}) => {
  const sign = reverse ? -1 : 1;
  (allocations || []).forEach((a) => {
    const id = String(a?.VendorBill_Id || a?.vendorBill_Id || "").trim();
    const amount = Number(a?.allocatedAmount) || 0;
    if (!id || amount <= 0) return;
    const existing = findVendorBillInState(
      getStore().getState()?.accountsPayableState,
      id
    );
    if (!existing) return;
    const total = Number(existing.total) || 0;
    const paid = Math.max(0, (Number(existing.paidAmount) || 0) + sign * amount);
    const due = Math.max(0, total - paid);
    const status = deriveVendorBillStatus(existing, paid, due);
    patchVendorBillInStore(id, {
      paidAmount: paid,
      dueAmount: due,
      status,
    });
  });
};

// ─── Payments ──────────────────────────────────────────────────────

export const vendorPaymentsPresentInStore = (apState) => {
  const state = apState || getStore().getState()?.accountsPayableState;
  return Boolean(state?.paymentsLoaded);
};

export const findVendorPaymentInState = (apState, id) => {
  const key = String(id ?? "").trim();
  if (!key || key === "null") return null;
  return (apState?.vendorPayments || []).find((p) => paymentKey(p) === key) || null;
};

export const upsertVendorPaymentInStore = (payment) => {
  if (!payment) return;
  const id = payment.POID || payment.id || payment.paymentId;
  if (!id) return;
  getStore().dispatch({
    type: "UPSERT_VENDOR_PAYMENT",
    payload: {
      ...payment,
      POID: id,
      id,
    },
  });
};

export const applyVendorPaymentsToStore = (list) => {
  getStore().dispatch({
    type: "SET_VENDOR_PAYMENTS",
    payload: Array.isArray(list) ? list : [],
  });
};

export const refreshVendorPaymentsInStore = async ({ silent = true } = {}) => {
  const list = await getVendorPayments({});
  applyVendorPaymentsToStore(list || []);
  return list || [];
};

export const ensureVendorPaymentsInStore = async ({
  force = false,
  silent = true,
} = {}) => {
  if (!force && vendorPaymentsPresentInStore()) {
    return getStore().getState()?.accountsPayableState?.vendorPayments || [];
  }
  return refreshVendorPaymentsInStore({ silent });
};

/** Bills for a vendor from Redux (open dues preferred). */
export const vendorBillsForVendor = (apState, vendorId) => {
  const key = String(vendorId ?? "").trim();
  if (!key) return [];
  return (apState?.vendorBills || []).filter(
    (b) => String(b.Vendor_Id || b.vendor_Id || "") === key
  );
};

/** Bills linked to a PO from Redux. */
export const vendorBillsForPurchaseOrder = (apState, purchaseOrderId) => {
  const key = String(purchaseOrderId ?? "").trim();
  if (!key) return [];
  return (apState?.vendorBills || []).filter(
    (b) => String(b.PurchaseOrder_Id || b.purchaseOrder_Id || "") === key
  );
};
